

































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { WaterMetersApi, WaterMonitorsApi } from '@/api';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ApiUtils } from '@/common/ApiUtils';
import Toast from '@/components/display/toast.vue';
import { WaterEntityService } from '@/services/water-entity-service';

@Component({
	components: {},
})
export default class WaterFlowCurrentStatus extends Vue {
	@Prop({ required: true }) public entityId!: number;
	@Prop({ required: true }) public graphId!: string;
	@Prop({ required: true }) public entityType!: string;

	public entityState: any = {};
	public chart;

	public graphData: any;

	private waterMetersApi: WaterMetersApi = new WaterMetersApi();
	private waterMonitorsApi: WaterMonitorsApi = new WaterMonitorsApi();
	private waterEntityService?: WaterEntityService;
	private readonly toast: Toast = new Toast();

	public async created() {
		await this.watchEntity();
		await this.loadCurrentState();
	}

	@Watch('entityId')
	@Watch('entityType')
	public async watchEntity() {
		this.waterEntityService = new WaterEntityService(this.entityType, this.entityId);
		await this.loadCurrentState();
	}

	public async loadCurrentState() {
		await this.loadGraphData();
		setTimeout(async () => await this.loadGauge(), 1);
	}

	public async loadGraphData() {
		const graphData = await this.waterEntityService?.getGraphData(1, 0);
		const currentState = await this.waterEntityService?.getCurrentState();

		const errors = [graphData?.error, currentState?.error].filter(e => e != null);
		if (errors.length > 0) {
			this.toast.makeToast(errors.join('\n'), 'Errors Occurred', 'danger');
			return;
		}

		this.graphData = graphData?.data;
		this.entityState = currentState?.data;

		this.entityState.max =
			Math.ceil(
				Math.max(
					...this.graphData.flow.map((a: any) => a.val),
					this.entityState.currentUsage ?? 10,
				),
			) || 1;
		this.entityState.lastReadingTimestamp =
			this.graphData?.flow?.length > 0 ? new Date(this.graphData.flow[0].date).getTime() : 0;
	}

	public async loadGauge() {
		if (this.chart) {
			await this.chart.dispose();
		}
		this.chart = am4core.create(`currentStatus ${this.graphId}`, am4charts.GaugeChart);
		this.chart.innerRadius = -40;
		this.chart.startAngle = 135;
		this.chart.endAngle = 405;

		// Create Legend
		let labelList = new am4core.ListTemplate(new am4core.Label());
		labelList.template.isMeasured = false;
		labelList.template.background.strokeWidth = 2;
		labelList.template.fontSize = 12;
		labelList.template.padding(5, 5, 5, 5);
		labelList.template.y = am4core.percent(50);
		labelList.template.horizontalCenter = 'middle';

		let labels: any[] = [
			{ y: 75, val: `${this.entityState.currentUsage?.toFixed(2) ?? 0}L/min` },
			{
				y: 88,
				val: `${this.$options.filters.dateTimeDiff(this.entityState.lastReadingTimestamp)}`,
			},
		];

		labels.forEach(L => {
			if (L.val && !L.val.startsWith('undefined')) {
				let label = labelList.create();
				label.parent = this.chart.chartContainer;
				label.x = am4core.percent(50);
				label.y = am4core.percent(L.y);
				label.background.stroke = new am4core.color('#808181');
				label.background.fill = label.background.stroke;
				label.fill = new am4core.color('#FFF');
				label.text = L.val;
			}
		});

		// Create axis
		const axis = this.chart.xAxes.push(new am4charts.ValueAxis<am4charts.AxisRendererCircular>());
		axis.min = 0;
		axis.max = this.entityState.max;
		axis.strictMinMax = true;
		axis.renderer.grid.template.disabled = true;
		axis.renderer.labels.template.disabled = true;
		axis.renderer.labels.template.fontSize = 10;
		axis.renderer.labels.template.radius = 5;
		axis.renderer.grid.template.color = am4core.color('#FFF');

		//Add labels
		let axis1Labels = [
			0,
			(Math.round(this.entityState.max * 0.25 * 100) * 0.01).toFixed(2),
			(Math.round(this.entityState.max * 0.5 * 100) * 0.01).toFixed(2),
			(Math.round(this.entityState.max * 0.75 * 100) * 0.01).toFixed(2),
			(Math.floor(this.entityState.max * 100) * 0.01).toFixed(2),
		];

		axis1Labels.forEach(label => {
			if (label || label === 0) {
				let axisRange = axis.axisRanges.create();
				axisRange.value = label;
				axisRange.label.text = label + ' L/min';
			}
		});

		let rangeValues: any[] = [
			{ start: 0, end: this.entityState.max * 0.25, color: '#536cb1' },
			{ start: this.entityState.max * 0.25, end: this.entityState.max * 0.5, color: '#8B76A2' },
			{ start: this.entityState.max * 0.5, end: this.entityState.max * 0.75, color: '#C48093' },
			{ start: this.entityState.max * 0.75, end: this.entityState.max, color: '#fc8a84' },
		];

		rangeValues.forEach(value => {
			let range = axis.axisRanges.create();
			range.value = value.start;
			range.endValue = value.end;
			range.axisFill.fillOpacity = 1;
			range.axisFill.fill = am4core.color(value.color);
			range.axisFill.zIndex = value.z;
		});

		// Add hand
		const hand = this.chart.hands.push(new am4charts.ClockHand());
		hand.value = this.entityState.currentUsage;
		hand.fill = am4core.color('#404040');
		hand.stroke = am4core.color('#FFF');
		hand.radius = am4core.percent(80);
		hand.startWidth = 10;
	}
}
